import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Network, { BaseURL } from '../utils/Network';
import useToast from './useToast';
import useSounds from './useSounds';
import useConfetti from './useConfetti';
import { setUserInfo } from '../redux/user/action';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { 
  PlayerTalent, 
  TalentUpgradeResponse,
  AvailableTalent,
  TalentApiContract,
  TalentInstantUpgradeResponse,
  UserTalentContract,
  UserTalentLevelContract,
  GetAllTalentsResponse,
  TalentCategoryContract,
  TalentCategoryResponse,
  TalentLevelStatus,
  TalentType,
  UpgradingTalentInfo,
} from '../types/talents';

interface TalentsHookReturn {
  // State
  talentTree: UserTalentContract[];
  flattenedTalents: PlayerTalent[];
  playerTalents: PlayerTalent[];
  availableTalents: AvailableTalent[];
  categories: TalentCategoryContract[];
  upgradingTalent: UpgradingTalentInfo | null;
  loading: boolean;
  upgrading: number | null;
  error?: string;
  
  // Actions
  fetchAllTalents: () => Promise<void>;
  fetchCategories: () => Promise<void>;
  upgradeTalent: (talentId: TalentType) => Promise<boolean>;
  upgradeTalentLevel: (talentLevelId: number) => Promise<boolean>;
  instantUpgradeTalent: (talentId: TalentType) => Promise<boolean>;
  instantUpgradeTalentLevel: (talentLevelId: number) => Promise<boolean>;
  refreshTalents: () => Promise<void>;
  
  // Utilities  
  getTalentById: (talentId: number) => PlayerTalent | undefined;
  getTalentByLevelId: (talentLevelId: number) => PlayerTalent | undefined;
  canUpgradeTalentLevel: (talentLevelId: number) => boolean;
  canUpgradeTalent: (talentId: number) => boolean;
  getAvailableTalentById: (talentId: number) => AvailableTalent | undefined;
  getTalentMaxLevel: (talentId: number) => number;
  getCurrentLevel: (talentId: number) => number;
  getNextAvailableLevel: (talentId: number) => UserTalentLevelContract | undefined;
}

type TalentsHookOptions = {
  enabled?: boolean;
  autoLoad?: boolean;
};

const usePlayerTalents = (options: TalentsHookOptions = {}): TalentsHookReturn => {
  const isEnabled = options.enabled ?? true;
  const shouldAutoLoad = options.autoLoad ?? true;

  const [talentTree, setTalentTree] = useState<UserTalentContract[]>([]);
  const [flattenedTalents, setFlattenedTalents] = useState<PlayerTalent[]>([]);
  const [playerTalents, setPlayerTalents] = useState<PlayerTalent[]>([]);
  const [availableTalents, setAvailableTalents] = useState<AvailableTalent[]>([]);
  const [categories, setCategories] = useState<TalentCategoryContract[]>([]);
  const [upgradingTalent, setUpgradingTalent] = useState<UpgradingTalentInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [upgrading, setUpgrading] = useState<number | null>(null);
  const [error, setError] = useState<string | undefined>();
  const userInfo = useSelector((state: any) => state.userInfo);
  const { t, currentLanguage } = useAppTranslation("toasts");

  /**
   * Flatten the tree structure into a list of PlayerTalent objects
   * This makes it easier for UI components to work with the data
   */
  const flattenTalentTree = useCallback((talents: UserTalentContract[], parentTalentId?: number, inheritedCategoryId?: number): PlayerTalent[] => {
    const result: PlayerTalent[] = [];
    
    for (const talent of talents) {
      // For each talent, get its highest active/available level
      const activeLevels = talent.levels.filter(l => l.isActive || l.status === TalentLevelStatus.Active);
      const isUpgradeableLevel = (level: UserTalentLevelContract) => (level.canActive || level.status === TalentLevelStatus.Available) && !level.isActive;
      const currentLevel = activeLevels.length > 0 
        ? Math.max(...activeLevels.map(l => l.level))
        : 0;
      
      // Get the next level that can be upgraded (first level with canActive = true and not active yet)
      const nextLevel = talent.levels.find(isUpgradeableLevel);
      const fallbackLevel = talent.levels[0];
      const levelToShow = nextLevel || activeLevels[activeLevels.length - 1] || fallbackLevel;
      if (!levelToShow) {
        continue;
      }

      const effectiveCategoryId = talent.categoryId ?? inheritedCategoryId;
      const effectDescription = levelToShow.description || levelToShow.name || talent.name;
      const description = talent.description || effectDescription;
      const hasActiveLevel = activeLevels.length > 0;

      result.push({
        talentId: talent.id,
        talentLevelId: levelToShow.id,
        level: currentLevel,
        name: talent.name,
        effect: effectDescription,
        description,
        imageUrl: talent.imageUrl,
        status: levelToShow.status,
        duration: levelToShow.duration,
        upgradeCost: levelToShow.upgradeCost,
        instantUpgradeCost: levelToShow.instantUpgradeCost,
        canActive: levelToShow.canActive || levelToShow.status === TalentLevelStatus.Available,
        isActive: hasActiveLevel || levelToShow.isActive,
        maxLevel: talent.levels.length,
        parentTalentId: talent.parentId ?? parentTalentId,
        hasChildren: talent.children && talent.children.length > 0,
        children: [],
        categoryId: effectiveCategoryId,
        researchBuildingRequiredLevel: talent.researchBuildingRequiredLevel,
      });
      
      // Recursively flatten children, passing down the categoryId
      if (talent.children && talent.children.length > 0) {
        result.push(...flattenTalentTree(talent.children, talent.id, talent.categoryId ?? inheritedCategoryId));
      }
    }
    
    return result;
  }, []);

  /**
   * Normalize the API payload into a tree structure
   */
  const buildTalentTree = useCallback((rawTalents: TalentApiContract[]): UserTalentContract[] => {
    if (!Array.isArray(rawTalents)) return [];

    const flatList: TalentApiContract[] = [];

    const mergeTalentLevels = (
      existingLevels: UserTalentLevelContract[] = [],
      incomingLevels: UserTalentLevelContract[] = []
    ): UserTalentLevelContract[] => {
      const levelMap = new Map<number, UserTalentLevelContract>();

      existingLevels.forEach((lvl) => levelMap.set(lvl.id, lvl));
      incomingLevels.forEach((lvl) => {
        const previous = levelMap.get(lvl.id);
        levelMap.set(lvl.id, { ...previous, ...lvl });
      });

      return Array.from(levelMap.values()).sort((a, b) => a.level - b.level);
    };

    const flattenRawNodes = (nodes: TalentApiContract[], parentId?: number) => {
      nodes.forEach((node) => {
        const normalizedParentId =
          typeof node.parentId === "number" ? node.parentId : parentId ?? 0;
        const normalizedNode: TalentApiContract = {
          ...node,
          parentId: normalizedParentId,
        };
        flatList.push(normalizedNode);

        const childNodes = Array.isArray(node.children)
          ? (node.children as TalentApiContract[]).filter(
              (child) => typeof child === "object" && child !== null
            )
          : [];

        if (childNodes.length > 0) {
          flattenRawNodes(childNodes, node.id);
        }
      });
    };

    flattenRawNodes(rawTalents);

    const talentMap = new Map<number, UserTalentContract>();

    flatList.forEach((raw) => {
      const existing = talentMap.get(raw.id);

      const normalizedLevels = Array.isArray(raw.levels)
        ? raw.levels
            .map((level) => ({
              id: level.id,
              name: level.name,
              description: level.description,
              registerDateTime: level.registerDateTime,
              status:
                typeof level.status === "number"
                  ? level.status
                  : TalentLevelStatus.Locked,
              level: typeof level.level === "number" ? level.level : 0,
              talentId: level.talentId,
              duration: level.duration ?? 0,
              canActive: !!level.canActive,
              isActive: !!level.isActive,
              upgradeCost: level.upgradeCost,
              instantUpgradeCost: level.instantUpgradeCost,
            }))
            .sort((a, b) => a.level - b.level)
        : [];

      const mergedLevels = existing
        ? mergeTalentLevels(existing.levels, normalizedLevels)
        : normalizedLevels;

      talentMap.set(raw.id, {
        id: raw.id,
        name: raw.name ?? existing?.name,
        description: raw.description ?? existing?.description,
        imageUrl: raw.imageUrl ?? existing?.imageUrl,
        categoryId:
          typeof raw.categoryId === "number"
            ? raw.categoryId
            : existing?.categoryId,
        type: typeof raw.type === "number" ? raw.type : existing?.type,
        parentId:
          typeof raw.parentId === "number" && raw.parentId > 0
            ? raw.parentId
            : existing?.parentId,
        researchBuildingRequiredLevel:
          typeof raw.researchBuildingRequiredLevel === "number"
            ? raw.researchBuildingRequiredLevel
            : existing?.researchBuildingRequiredLevel,
        children: [],
        levels: mergedLevels,
      });
    });

    const roots: UserTalentContract[] = [];

    talentMap.forEach((talent) => {
      const parentId = talent.parentId;
      if (parentId && talentMap.has(parentId)) {
        talentMap.get(parentId)!.children.push(talent);
      } else {
        roots.push(talent);
      }
    });

    return roots;
  }, []);

  const findUpgradingTalent = useCallback((tree: UserTalentContract[]): UpgradingTalentInfo | null => {
    const candidates: Array<{ talent: UserTalentContract; level: UserTalentLevelContract }> = [];

    const visit = (node: UserTalentContract) => {
      node.levels.forEach((level) => {
        const isUpgrading =
          !level.isActive &&
          (level.status === TalentLevelStatus.Upgrading ||
            !!level.registerDateTime);
        if (isUpgrading) {
          candidates.push({ talent: node, level });
        }
      });
      node.children?.forEach(visit);
    };

    tree.forEach(visit);
    if (!candidates.length) return null;

    candidates.sort((a, b) => {
      const aTime = a.level.registerDateTime ? new Date(a.level.registerDateTime).getTime() : 0;
      const bTime = b.level.registerDateTime ? new Date(b.level.registerDateTime).getTime() : 0;
      return bTime - aTime;
    });

    const { talent, level } = candidates[0];
    return {
      talentId: talent.id,
      talentName: talent.name,
      levelId: level.id,
      levelName: level.name,
      duration: level.duration,
      registerDateTime: level.registerDateTime,
      upgradeCost: level.upgradeCost,
      instantUpgradeCost: level.instantUpgradeCost,
    };
  }, []);

  /**
   * Build legacy-friendly collections so older components keep working
   */
  const rebuildLegacyCollections = useCallback((
    tree: UserTalentContract[],
    flattened: PlayerTalent[]
  ) => {
    const findInTree = (nodes: UserTalentContract[], talentId: number): UserTalentContract | undefined => {
      for (const node of nodes) {
        if (node.id === talentId) return node;
        if (node.children && node.children.length > 0) {
          const found = findInTree(node.children, talentId);
          if (found) return found;
        }
      }
      return undefined;
    };

    setPlayerTalents(flattened);
    const userCleg = userInfo?.totalCleg ?? 0;
    const userBloodGems = userInfo?.totalBloodGem ?? 0;

    const legacyAvailable = flattened.map((talent) => {
      const treeNode = findInTree(tree, talent.talentId);
      const nextLevel = treeNode?.levels.find(
        (level) => (level.canActive || level.status === TalentLevelStatus.Available) && !level.isActive
      );
      const costCleg = nextLevel?.upgradeCost ?? 0;
      const costBloodGems = nextLevel?.instantUpgradeCost;
      const canAfford =
        (costCleg ? userCleg >= costCleg : true) &&
        (typeof costBloodGems === "number"
          ? userBloodGems >= costBloodGems
          : true);
      const buildingRequiredLevel = treeNode?.researchBuildingRequiredLevel ?? 0;

      return {
        talentId: talent.talentId,
        name: talent.name,
        description: talent.description,
        requirements: {},
        cost: {
          cleg: costCleg,
          bloodGems: costBloodGems,
        },
        isAvailable: !!nextLevel,
        canAfford,
        maxLevel: talent.maxLevel,
        currentLevel: talent.level,
        buildingRequiredLevel,
        amount: talent.amount ?? 0,
        categoryId: talent.categoryId ?? 0,
      } as AvailableTalent;
    });

    setAvailableTalents(legacyAvailable);
  }, [userInfo]);
  
  const axios = new Network();
  const dispatch = useDispatch();
  const notify = useToast();
  const { playSound } = useSounds();
  const { showConfetti } = useConfetti();
  
  // Get user data for resource checks

  /**
   * Fetch the complete talent tree from the talents API
   * Endpoint: GET /api/v2/playertalentlevel/getalltalents
   */
  const fetchAllTalents = useCallback(async () => {
    if (!isEnabled) return;
    try {
      setLoading(true);
      setError(undefined);
      
      const response = await axios.CallApi<GetAllTalentsResponse>({
        url: "/api/v2/playertalentlevel/getalltalents",
        customHost: BaseURL,
        method: "get",
      });
      
      const rawTalents = response.data?.result;

      if (response.ok && Array.isArray(rawTalents)) {
        const normalizedTree = buildTalentTree(rawTalents);
        setTalentTree(normalizedTree);
        setUpgradingTalent(findUpgradingTalent(normalizedTree));
        
        // Flatten the tree for easier UI access
        const flattened = flattenTalentTree(normalizedTree);
        setFlattenedTalents(flattened);
        rebuildLegacyCollections(normalizedTree, flattened);
      } else {
        const message =
          response.data?.message ||
          response.message ||
          t("talents.fetchTreeFailed");
        setError(message);
        notify({ ...response, message });
      }
    } catch (err: any) {
      const message = err.message || t("network.error");
      setError(message);
      notify({ ok: false, message: t("talents.loadFailed") });
    } finally {
      setLoading(false);
    }
  }, [axios, notify, buildTalentTree, flattenTalentTree, rebuildLegacyCollections, isEnabled, t]);

  /**
   * Fetch talent categories from the new v2 API
   * Endpoint: GET /api/v2/talentcategory/getall
   */
  const fetchCategories = useCallback(async () => {
    if (!isEnabled) return;
    try {
      const response = await axios.CallApi<TalentCategoryResponse>({
        url: "/api/v2/talentcategory/getall",
        customHost: BaseURL,
        method: "get",
      });
      
      if (response.ok && response.data && response.data.result && Array.isArray(response.data.result.result)) {
        setCategories(response.data.result.result);
      } else {
        console.warn("Failed to fetch talent categories:", response.message);
      }
    } catch (err: any) {
      console.warn("Failed to load talent categories:", err.message);
    }
  }, [axios, isEnabled]);

  const refreshUserData = async () => {
    try {
      const response = await axios.CallApi({
        url: "/player",
        method: "get",
      });
      if (response.ok) {
        dispatch(setUserInfo(response.data));
      }
    } catch (err) {
      console.error('Failed to refresh user data:', err);
    }
  };

  /**
   * Upgrade a specific talent level
   * Endpoint: POST /api/v2/playertalentlevel/upgradetalent?talentLevelId={id}
   * @param talentLevelId - The ID of the talent level to upgrade (from level.id)
   */
  const executeTalentUpgrade = useCallback(async (talentLevelId: number, upgradingId?: number): Promise<boolean> => {
    if (upgrading === talentLevelId || (typeof upgradingId === "number" && upgrading === upgradingId)) {
      const message = t("talents.alreadyUpgrading");
      setError(message);
      notify({ ok: false, message });
      return false;
    }

    try {
      setUpgrading(typeof upgradingId === "number" ? upgradingId : talentLevelId);
      setError(undefined);

      const response = await axios.CallApi<TalentUpgradeResponse>({
        url: `/api/v2/playertalentlevel/upgradetalent?talentLevelId=${talentLevelId}`,
        customHost: BaseURL,
        method: "post",
      });
      
      if (response.ok) {
        // Show success notification for successful upgrade
        notify({ 
          ok: true, 
          message: t("talents.upgradeSuccess") 
        });
        
        // Play success effects
        playSound('quest-done');
        showConfetti({ duration: 1.5 });
        
        // Refresh data to get updated talent information
        await Promise.all([
          fetchAllTalents(),
          refreshUserData()
        ]);
        
        return true;
      } else {
        const message =
          response.data?.message ||
          response.message ||
          t("talents.upgradeFailed");
        notify({ ...response, message });
        setError(message);
        return false;
      }
    } catch (err: any) {
      const message = err.message || t("network.error");
      setError(message);
      notify({ ok: false, message: t("talents.upgradeFailed") });
      return false;
    } finally {
      setUpgrading(null);
    }
  }, [axios, notify, playSound, showConfetti, fetchAllTalents, refreshUserData, upgrading, t]);

  const upgradeTalentLevel = useCallback(async (talentLevelId: number): Promise<boolean> => {
    return executeTalentUpgrade(talentLevelId);
  }, [executeTalentUpgrade]);

  /**
   * Instant-upgrade a specific talent levele
   * Endpoint: POST /api/v2/playertalentlevel/instantupgradetalent?talentLevelId={id}
   */
  const instantUpgradeTalentLevel = useCallback(async (talentLevelId: number): Promise<boolean> => {
    if (upgrading === talentLevelId) {
      const message = t("talents.alreadyUpgrading");
      setError(message);
      notify({ ok: false, message });
      return false;
    }

    try {
      setUpgrading(talentLevelId);
      setError(undefined);

      const response = await axios.CallApi<TalentInstantUpgradeResponse>({
        url: `/api/v2/playertalentlevel/instantupgradetalent?talentLevelId=${talentLevelId}`,
        customHost: BaseURL,
        method: "post",
      });

      const isSuccess = response.ok && (response.data?.value ?? 0) >= 1;

      if (isSuccess) {
        const message = response.data?.message || t("talents.instantUpgradeSuccess");
        notify({ ok: true, message });
        playSound('quest-done');
        showConfetti({ duration: 1.5 });
        await Promise.all([
          fetchAllTalents(),
          refreshUserData()
        ]);
        return true;
      } else {
        const message =
          response.data?.message ||
          response.message ||
          t("talents.instantUpgradeFailed");
        notify({ ...response, message });
        setError(message);
        return false;
      }
    } catch (err: any) {
      const message = err.message || t("network.error");
      setError(message);
      notify({ ok: false, message: t("talents.instantUpgradeFailed") });
      return false;
    } finally {
      setUpgrading(null);
    }
  }, [axios, notify, playSound, showConfetti, fetchAllTalents, refreshUserData, upgrading, t]);

  const refreshTalents = useCallback(async () => {
    await Promise.all([
      fetchAllTalents(),
      fetchCategories()
    ]);
  }, [fetchAllTalents, fetchCategories]);

  // Utility functions

  /**
   * Find a talent in the tree by its talentId
   */
  const findTalentInTree = useCallback((talents: UserTalentContract[], talentId: number): UserTalentContract | undefined => {
    for (const talent of talents) {
      if (talent.id === talentId) {
        return talent;
      }
      if (talent.children && talent.children.length > 0) {
        const found = findTalentInTree(talent.children, talentId);
        if (found) return found;
      }
    }
    return undefined;
  }, []);

  /**
   * Get a flattened talent by its talentId
   */
  const getTalentById = useCallback((talentId: number): PlayerTalent | undefined => {
    return flattenedTalents.find(t => t.talentId === talentId);
  }, [flattenedTalents]);

  /**
   * Get a talent by a specific level ID
   */
  const getTalentByLevelId = useCallback((talentLevelId: number): PlayerTalent | undefined => {
    return flattenedTalents.find(t => t.talentLevelId === talentLevelId);
  }, [flattenedTalents]);

  /**
   * Check if a specific talent level can be upgraded
   */
  const canUpgradeTalentLevel = useCallback((talentLevelId: number): boolean => {
    const talent = getTalentByLevelId(talentLevelId);
    if (!talent) return false;
    
    // Check if the talent level can be activated
    return !talent.isActive && (talent.canActive || talent.status === TalentLevelStatus.Available);
  }, [getTalentByLevelId]);

  /**
   * Get the maximum level for a talent
   */
  const getTalentMaxLevel = useCallback((talentId: number): number => {
    const talentNode = findTalentInTree(talentTree, talentId);
    return talentNode?.levels.length || 1;
  }, [talentTree, findTalentInTree]);

  /**
   * Get the current active level for a talent
   */
  const getCurrentLevel = useCallback((talentId: number): number => {
    const talentNode = findTalentInTree(talentTree, talentId);
    if (!talentNode) return 0;
    
    const activeLevels = talentNode.levels.filter(l => l.isActive || l.status === TalentLevelStatus.Active);
    if (activeLevels.length === 0) return 0;
    
    return Math.max(...activeLevels.map(l => l.level));
  }, [talentTree, findTalentInTree]);

  /**
   * Get the next level that can be activated for a talent
   */
  const getNextAvailableLevel = useCallback((talentId: number): UserTalentLevelContract | undefined => {
    const talentNode = findTalentInTree(talentTree, talentId);
    if (!talentNode) return undefined;
    
    return talentNode.levels.find(l => (l.canActive || l.status === TalentLevelStatus.Available) && !l.isActive);
  }, [talentTree, findTalentInTree]);

  const getAvailableTalentById = useCallback((talentId: number): AvailableTalent | undefined => {
    return availableTalents.find((talent) => talent.talentId === talentId);
  }, [availableTalents]);

  const canUpgradeTalent = useCallback((talentId: number): boolean => {
    const nextLevel = getNextAvailableLevel(talentId);
    return !!(nextLevel && !nextLevel.isActive && (nextLevel.canActive || nextLevel.status === TalentLevelStatus.Available));
  }, [getNextAvailableLevel]);

  const upgradeTalent = useCallback(async (talentId: TalentType): Promise<boolean> => {
    const nextLevel = getNextAvailableLevel(talentId);
    if (!nextLevel) {
      setError("No upgradeable level available for this talent");
      return false;
    }

    return executeTalentUpgrade(nextLevel.id, talentId);
  }, [executeTalentUpgrade, getNextAvailableLevel]);

  const instantUpgradeTalent = useCallback(async (talentId: TalentType): Promise<boolean> => {
    const nextLevel = getNextAvailableLevel(talentId);
    if (!nextLevel) {
      setError("No upgradeable level available for this talent");
      return false;
    }

    return instantUpgradeTalentLevel(nextLevel.id);
  }, [getNextAvailableLevel, instantUpgradeTalentLevel]);

  // Auto-load talents on mount, and again when the language changes: the server writes their names in it
  useEffect(() => {
    if (!shouldAutoLoad || !isEnabled) return;
    refreshTalents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoLoad, isEnabled, currentLanguage]);

  return {
    // State
    talentTree,
    flattenedTalents,
    playerTalents,
    availableTalents,
    categories,
    upgradingTalent,
    loading,
    upgrading,
    error,
    
    // Actions
    fetchAllTalents,
    fetchCategories,
    upgradeTalent,
    upgradeTalentLevel,
    instantUpgradeTalent,
    instantUpgradeTalentLevel,
    refreshTalents,
    
    // Utilities
    getAvailableTalentById,
    getTalentById,
    getTalentByLevelId,
    canUpgradeTalent,
    canUpgradeTalentLevel,
    getTalentMaxLevel,
    getCurrentLevel,
    getNextAvailableLevel,
  };
};

export default usePlayerTalents;
